/**
 * verificationApi.js
 *
 * S3-presigned-upload helpers for sensitive applicant documents (currently:
 * rental-application proof-of-income). Flow:
 *  1. Call the existing getS3UploadUrl Lambda to get a presigned POST URL.
 *  2. Upload the file directly from the browser to S3 using that presigned POST.
 *  3. Store the resulting S3 key + metadata in Firestore.
 *
 * Auth: All API calls (except the direct-to-S3 presigned POST upload) go
 * through apiFetch, which sends the HttpOnly firebaseToken cookie set by
 * the /login Lambda and transparently refreshes it (or signs the user out)
 * if the cookie has gone stale.
 */
import { apiFetch } from "./apiFetch";

const API_BASE_URL =
  import.meta.env.VITE_APP_API_URL || "https://auth.ezron.ai";

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Ask the getS3UploadUrl Lambda for a presigned POST for the given filename.
 * The Lambda prepends {uid}/ to the key automatically.
 * @param {string} filename  Path relative to uid/, e.g. "verifications/{propertyId}/id-123.pdf"
 * @returns {Promise<{ postUrl: string, fields: Object, key: string }>}
 */
async function getUploadUrl(filename) {
  const url = `${API_BASE_URL}/getS3UploadUrl?filename=${encodeURIComponent(filename)}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getS3UploadUrl returned ${res.status}`);
  }
  const data = await res.json();
  // data.fields.key is the full S3 key including the uid prefix
  return { postUrl: data.postUrl, fields: data.fields, key: data.fields.key };
}

/**
 * Upload a single File to S3 using a presigned POST.
 * No Authorization header — the presigned POST fields carry the credentials.
 * @param {string} postUrl
 * @param {Object} fields   Key-value pairs from the presigned POST
 * @param {File}   file
 */
async function uploadToS3(postUrl, fields, file) {
  const form = new FormData();
  // Presigned POST fields must come before the file
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  form.append("file", file);

  const res = await fetch(postUrl, { method: "POST", body: form });
  if (!res.ok) {
    // S3 returns XML errors, try to pull out the Code element
    const text = await res.text().catch(() => "");
    const codeMatch = text.match(/<Code>(.+?)<\/Code>/);
    throw new Error(codeMatch ? codeMatch[1] : `S3 upload failed with status ${res.status}`);
  }
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Upload one proof-of-income document (pay stub, bank statement, etc.) for a
 * rental application and return the metadata to store on the application doc.
 * @param {string} applicationId
 * @param {File}   file
 * @returns {Promise<{ key: string, filename: string, size: number, uploadedAt: string }>}
 */
export async function uploadIncomeDocument(applicationId, file) {
  const ts = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const filename = `applications/${applicationId}/income/${ts}-${safeName}`;
  const { postUrl, fields, key } = await getUploadUrl(filename);
  await uploadToS3(postUrl, fields, file);
  return { key, filename: file.name, size: file.size, uploadedAt: new Date().toISOString() };
}

/**
 * Get a short-lived presigned GET URL for a proof-of-income document.
 * Authorized for the applicant themself, the property owner, or an admin —
 * see getApplicationDocumentUrl.py.
 * @param {string} applicationId
 * @param {string} key  Full S3 key
 * @returns {Promise<string>}  The presigned URL
 */
export async function getIncomeDocumentUrl(applicationId, key) {
  const url = `${API_BASE_URL}/getApplicationDocumentUrl?applicationId=${encodeURIComponent(applicationId)}&key=${encodeURIComponent(key)}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getApplicationDocumentUrl returned ${res.status}`);
  }
  const data = await res.json();
  return data.url;
}

/**
 * Same as uploadIncomeDocument, scoped to one co-applicant's sub-doc.
 * @param {string} applicationId
 * @param {string} applicantId
 * @param {File}   file
 * @returns {Promise<{ key: string, filename: string, size: number, uploadedAt: string }>}
 */
export async function uploadApplicantIncomeDocument(applicationId, applicantId, file) {
  const ts = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const filename = `applications/${applicationId}/applicants/${applicantId}/income/${ts}-${safeName}`;
  const { postUrl, fields, key } = await getUploadUrl(filename);
  await uploadToS3(postUrl, fields, file);
  return { key, filename: file.name, size: file.size, uploadedAt: new Date().toISOString() };
}

/**
 * Same as getIncomeDocumentUrl, scoped to one co-applicant's sub-doc.
 * @param {string} applicationId
 * @param {string} applicantId
 * @param {string} key  Full S3 key
 * @returns {Promise<string>}  The presigned URL
 */
export async function getApplicantIncomeDocumentUrl(applicationId, applicantId, key) {
  const url = `${API_BASE_URL}/getApplicationDocumentUrl?applicationId=${encodeURIComponent(applicationId)}&applicantId=${encodeURIComponent(applicantId)}&key=${encodeURIComponent(key)}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `getApplicationDocumentUrl returned ${res.status}`);
  }
  const data = await res.json();
  return data.url;
}


